import { useCallback, useEffect, useState } from "react";
import { isEmpty } from "lodash";

import i18next, { loadStaticTranslations } from "~/common/i18n";
import { LocaleCode, LocaleCodes } from "~/common/types/common";
import { setFormattingLocale } from "~/util/format";

const LANGUAGE_STORAGE_KEY = "selectedLanguage";

const getStoredLanguage = (): LocaleCodes | null => {
  try {
    const stored = sessionStorage.getItem(LANGUAGE_STORAGE_KEY);
    return stored ? (JSON.parse(stored) as LocaleCodes) : null;
  } catch {
    return null;
  }
};

const setStoredLanguage = (language: LocaleCodes) => {
  try {
    sessionStorage.setItem(LANGUAGE_STORAGE_KEY, JSON.stringify(language));
  } catch {
    // Ignore storage errors
  }
};

export type UseLanguageProps = {
  merchantLanguages: LocaleCodes[];
};

export const useLanguage = ({ merchantLanguages }: UseLanguageProps) => {
  const [language, setLanguage] = useState<LocaleCodes>(
    () => getStoredLanguage() || LocaleCode.en
  );

  const changeI18nextLanguage = useCallback((updatedLanguage: LocaleCodes) => {
    i18next.changeLanguage(updatedLanguage);
  }, []);

  const changeDropdownLanguage = useCallback((updatedLanguage: LocaleCodes) => {
    setLanguage(updatedLanguage);
    setFormattingLocale(updatedLanguage);
    setStoredLanguage(updatedLanguage);
  }, []);

  const changeLanguage = useCallback((updatedLanguage: LocaleCodes) => {
    changeI18nextLanguage(updatedLanguage);
    changeDropdownLanguage(updatedLanguage);
  }, [changeI18nextLanguage, changeDropdownLanguage]);

  // Sync i18next and formatting locale with stored language immediately on mount
  // This prevents desync before merchantLanguages becomes available
  useEffect(() => {
    const storedLanguage = getStoredLanguage();
    if (storedLanguage) {
      i18next.changeLanguage(storedLanguage);
      setFormattingLocale(storedLanguage);
    }
  }, []);

  useEffect(() => {
    if (isEmpty(merchantLanguages)) return;

    loadStaticTranslations(merchantLanguages);

    // First, check if there's a stored language preference that's valid for this merchant
    const storedLanguage = getStoredLanguage();
    if (storedLanguage && merchantLanguages.includes(storedLanguage)) {
      changeLanguage(storedLanguage);
      return;
    }

    // Otherwise, use the browser's detected language
    const i18nextLanguage = i18next.languages?.[0] as LocaleCodes;
    if (i18nextLanguage) {
      if (merchantLanguages.includes(i18nextLanguage)) {
        changeLanguage(i18nextLanguage);
      } else {
        changeLanguage(LocaleCode.en as LocaleCodes);
      }
    }
  }, [merchantLanguages, changeLanguage]);

  const alignI18nextAndDropdownLanguage = useCallback(
    (i18nextLanguage: LocaleCodes) => {
      if (merchantLanguages.includes(i18nextLanguage)) {
        changeDropdownLanguage(i18nextLanguage);
      } else {
        changeLanguage(LocaleCode.en as LocaleCodes);
      }
    },
    [merchantLanguages, changeDropdownLanguage, changeLanguage]
  );

  const i18nextOnInitialized = useCallback(() => {
    if (isEmpty(merchantLanguages)) return;
    const i18nextLanguage = i18next.languages[0] as LocaleCodes;
    alignI18nextAndDropdownLanguage(i18nextLanguage);
  }, [merchantLanguages, alignI18nextAndDropdownLanguage]);

  useEffect(() => {
    i18next.on("initialized", i18nextOnInitialized);
    return () => i18next.off("initialized", i18nextOnInitialized);
  }, [i18nextOnInitialized]);

  return {
    language,
    changeLanguage,
  };
};
